import * as React from "react"
import { cn } from "@/lib/utils"

type AvatarContextValue = {
  isImageLoaded: boolean
  setIsImageLoaded: (value: boolean) => void
}

const AvatarContext = React.createContext<AvatarContextValue | undefined>(undefined)

const Avatar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const [isImageLoaded, setIsImageLoaded] = React.useState(false)

  return (
    <AvatarContext.Provider value={{ isImageLoaded, setIsImageLoaded }}>
      <div
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
          className
        )}
        {...props}
      />
    </AvatarContext.Provider>
  )
})
Avatar.displayName = "Avatar"

const AvatarImage = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>(({ className, src, ...props }, ref) => {
  const context = React.useContext(AvatarContext)

  React.useEffect(() => {
    if (!src) {
      context?.setIsImageLoaded(false)
      return
    }
    const img = new Image()
    img.src = src
    img.onload = () => context?.setIsImageLoaded(true)
    img.onerror = () => context?.setIsImageLoaded(false)
  }, [src, context])

  // Hide image if not loaded to prevent broken icon or layout shift
  if (!context?.isImageLoaded) return null

  return (
    <img
      ref={ref}
      src={src}
      className={cn("aspect-square h-full w-full", className)}
      {...props}
    />
  )
})
AvatarImage.displayName = "AvatarImage"

const AvatarFallback = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const context = React.useContext(AvatarContext)

  // Show fallback if image is NOT loaded
  // Note: This means fallback shows during loading too, which is good.
  if (context?.isImageLoaded) return null

  return (
    <div
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center rounded-full bg-muted",
        className
      )}
      {...props}
    />
  )
})
AvatarFallback.displayName = "AvatarFallback"

export { Avatar, AvatarImage, AvatarFallback }
